import { useParams, Link } from "react-router-dom";
import NotFoundPage from "@/pages/NotFoundPage";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BadgeCheck, MapPin, Wifi, Building2, ArrowLeft, ArrowRight, Globe, Instagram,
  Phone, Mail, GraduationCap, ShieldCheck, Sparkles, Check, Languages, FileText, AlertCircle,
} from "lucide-react";
import {
  PROFISSIONAIS_EXEMPLO, CATEGORIA_COR, COR_CLASSES, getInitials, buildPerfil,
} from "@/data/profissionaisData";
import Seo from "@/components/Seo";

const Block = ({ icon: Icon, title, children, testid }) => (
  <motion.div
    data-testid={testid}
    className="bg-white rounded-3xl border border-maternnia-nevoa/60 p-7 md:p-8 shadow-sm"
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="flex items-center gap-2.5 mb-5">
      <Icon size={19} className="text-maternnia-eucalipto" />
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium">{title}</h2>
    </div>
    {children}
  </motion.div>
);

const PerfilProfissionalPage = () => {
  const { id } = useParams();
  const base = PROFISSIONAIS_EXEMPLO.find((p) => p.id === id);
  if (!base) return <NotFoundPage />;

  const p = buildPerfil(base);
  const cor = COR_CLASSES[CATEGORIA_COR[p.categoria]] || COR_CLASSES.pink;

  const interesse = () =>
    toast.success("Interesse enviado!", {
      description: `Em breve o contato com ${p.nome} estará disponível. (Exemplo de demonstração.)`,
    });

  return (
    <main data-testid="perfil-page" className="bg-maternnia-porcelana/40 min-h-screen pb-24">
      <Seo
        title={`${p.nome} — ${p.categoria}`}
        path={`/profissionais/${p.id}`}
        description={`${p.nome}, ${p.categoria}${p.cidade ? ` em ${p.cidade}` : ""}. Perfil profissional na Maternnia.`}
        noindex
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: p.nome,
          jobTitle: p.categoria,
          address: p.cidade,
        }}
      />
      {/* FIRST FOLD */}
      <section className={`${cor.bg} relative`}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-8 md:pt-10 pb-12 md:pb-16">
          <Link to="/profissionais" data-testid="perfil-voltar" className="inline-flex items-center gap-2 text-sm text-maternnia-taupe hover:text-maternnia-charcoal transition-colors mb-8">
            <ArrowLeft size={16} /> Voltar ao diretório
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className={`h-24 w-24 rounded-3xl bg-white ${cor.text} flex items-center justify-center font-serif text-4xl font-medium shrink-0 shadow-sm`}>
              {getInitials(p.nome)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 data-testid="perfil-nome" className="font-serif text-4xl md:text-5xl tracking-tight text-maternnia-charcoal font-medium">{p.nome}</h1>
                {p.verificado && <BadgeCheck size={24} className="text-maternnia-eucalipto" data-testid="perfil-verificado" />}
              </div>
              <p className={`mt-1 text-sm font-semibold uppercase tracking-wide ${cor.text}`}>{p.categoria}</p>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-maternnia-taupe"><MapPin size={15} /> {p.bairro}, {p.cidade}</span>
                {p.online && <span className="inline-flex items-center gap-1.5 bg-white/70 text-maternnia-eucalipto rounded-full px-3 py-1 text-xs font-medium"><Wifi size={13} /> Online</span>}
                {p.presencial && <span className="inline-flex items-center gap-1.5 bg-white/70 text-maternnia-taupe rounded-full px-3 py-1 text-xs font-medium"><Building2 size={13} /> Presencial</span>}
              </div>
            </div>
          </div>

          <p className="mt-6 text-base md:text-lg text-maternnia-charcoal/80 leading-relaxed font-light max-w-2xl">{p.descricao}</p>

          <div className="flex flex-col sm:flex-row gap-3 mt-7">
            <button
              data-testid="perfil-cta-interesse"
              onClick={interesse}
              className="group inline-flex items-center justify-center gap-2 bg-maternnia-pink text-white hover:bg-maternnia-pinkHover rounded-full px-8 py-4 text-base font-medium transition-all duration-300 shadow-sm hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            >
              Tenho interesse
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a data-testid="perfil-site" href="#contato" className="inline-flex items-center justify-center gap-2 bg-white text-maternnia-charcoal hover:bg-maternnia-nevoa/40 rounded-full px-6 py-4 text-sm font-medium transition-all duration-300">
              <Globe size={16} /> Site
            </a>
            <a data-testid="perfil-instagram" href="#contato" className="inline-flex items-center justify-center gap-2 bg-white text-maternnia-charcoal hover:bg-maternnia-nevoa/40 rounded-full px-6 py-4 text-sm font-medium transition-all duration-300">
              <Instagram size={16} /> Instagram
            </a>
          </div>
        </div>
      </section>

      {/* BODY */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Block icon={Sparkles} title="Especialidades" testid="perfil-especialidades">
            <div className="flex flex-wrap gap-2">
              {p.especialidades.map((e) => (
                <span key={e} className={`text-sm font-medium rounded-full px-4 py-2 ${cor.bg} ${cor.text}`}>{e}</span>
              ))}
            </div>
          </Block>

          <Block icon={Check} title="Serviços" testid="perfil-servicos">
            <ul className="space-y-3">
              {p.servicos.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm text-maternnia-charcoal">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-maternnia-eucalipto/10 flex items-center justify-center shrink-0"><Check size={12} className="text-maternnia-eucalipto" /></span>
                  {s}
                </li>
              ))}
            </ul>
          </Block>

          <Block icon={Sparkles} title="Diferenciais" testid="perfil-diferenciais">
            <ul className="space-y-3">
              {p.diferenciais.map((d) => (
                <li key={d} className="flex items-start gap-3 text-sm text-maternnia-charcoal">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-maternnia-pink shrink-0" />
                  {d}
                </li>
              ))}
            </ul>
          </Block>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Block icon={GraduationCap} title="Formação" testid="perfil-formacao">
              <ul className="space-y-3">
                {p.formacao.map((f) => (
                  <li key={f} className="text-sm text-maternnia-taupe font-light leading-relaxed">{f}</li>
                ))}
              </ul>
            </Block>
            <Block icon={ShieldCheck} title="Credenciais" testid="perfil-credenciais">
              <ul className="space-y-3">
                {p.credenciais.map((c) => (
                  <li key={c} className="flex items-center gap-2 text-sm text-maternnia-charcoal">
                    <ShieldCheck size={14} className="text-maternnia-eucalipto" /> {c}
                  </li>
                ))}
              </ul>
            </Block>
          </div>

          <Block icon={FileText} title="Conteúdos publicados" testid="perfil-conteudos">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {p.conteudos.map((c, i) => (
                <a key={i} href="/#conteudos" className="group block rounded-2xl border border-maternnia-nevoa/60 p-5 hover:bg-maternnia-porcelana/50 transition-colors">
                  <h4 className="font-serif text-lg text-maternnia-charcoal font-medium leading-snug group-hover:text-maternnia-eucalipto transition-colors">{c.title}</h4>
                  <span className="text-xs text-maternnia-taupe font-light">Leitura de {c.min}</span>
                </a>
              ))}
            </div>
          </Block>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-6">
          <Block icon={Languages} title="Idiomas" testid="perfil-idiomas">
            <div className="flex flex-wrap gap-2">
              {p.idiomas.map((i) => (
                <span key={i} className="text-sm font-medium rounded-full px-4 py-2 bg-maternnia-porcelana text-maternnia-taupe">{i}</span>
              ))}
            </div>
          </Block>

          <div id="contato">
            <Block icon={Phone} title="Contato" testid="perfil-contato">
              <ul className="space-y-3 text-sm text-maternnia-charcoal">
                <li className="flex items-center gap-2.5"><Phone size={15} className="text-maternnia-taupe" /> {p.contato.telefone}</li>
                <li className="flex items-center gap-2.5"><Mail size={15} className="text-maternnia-taupe" /> {p.contato.email}</li>
                <li className="flex items-center gap-2.5"><Globe size={15} className="text-maternnia-taupe" /> {p.contato.site}</li>
                <li className="flex items-center gap-2.5"><Instagram size={15} className="text-maternnia-taupe" /> {p.contato.instagram}</li>
              </ul>
              <button
                onClick={interesse}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-maternnia-charcoal text-white hover:bg-maternnia-pink rounded-full px-6 py-3 text-sm font-medium transition-all duration-300"
              >
                Tenho interesse <ArrowRight size={16} />
              </button>
            </Block>
          </div>

          {/* DISCLAIMER */}
          <div data-testid="perfil-disclaimer" className="bg-maternnia-nevoa/40 rounded-3xl p-6 flex gap-3">
            <AlertCircle size={18} className="text-maternnia-taupe shrink-0 mt-0.5" />
            <p className="text-xs text-maternnia-taupe leading-relaxed font-light">
              A Maternnia facilita o acesso a informações e profissionais, mas não garante resultados clínicos ou comerciais. Perfil de exemplo para demonstração.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PerfilProfissionalPage;
